import Tabs from "components/EvtKit/tabs/Tabs"
import TabsContent from "components/EvtKit/tabs/TabsContent"
import Benefits from "./components/v2/Benefits"
import PlanSummary from "./components/v2/PlanSummary"
import CreatePlanTabList from "./components/v2/tabs/CreatePlanTabList"
import PageLayout from "components/PageLayout"
import PurchasePlanForm from "./components/v2/PurchasePlanForm"
import usePurchasePlan from "./hooks/usePurchasePlan"
import { planFormHeadingText, tabIds } from "./constants/defaultValues"
import SimplePricingVariablesList from "./components/v2/SimplePricingVariablesList"
import { useQueryState } from "context/queryState"
import ApiPurchasePlans from "api/ApiPurchasePlans"
import { useAppSelector } from "reducers/store"
import { PURCHASE_PLAN_INFO } from "reducers/purchasePlan/purchase-plan-actions"
import BodyText from "components/Typography/BodyText"
import usePurchasePlanRequest from "./hooks/usePurchasePlanRequest"
import { mapPurchasePlanToSubmition } from "./mappers/mapPurchasePlanForm"

import { memo, useCallback, useEffect } from "react"
import styled from "styled-components"
import { useNavigate, useParams } from "react-router-dom"
import { useQueryClient } from "react-query"
import DevModePricingVariables from "./components/v2/DevModePricingVariables"
import { usePurchasePlanIdentifiers } from "./hooks/usePurchasePlanIdentifiers"

const ContentContainer = styled.div`
  display: flex;
  width: 98%;
  gap: 8px;
  margin: 43px 0 20px 0;
`

const CardContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: ${(props) => props.theme.palette.white};
  padding: 16px 16px 0px 16px;
  box-shadow: 0px 2px 6px 0px #0000001a;
`

const ErrorContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  padding: 48px;
`

const PurchasePlanUpdate = () => {
  const { purchasePlanId } = useParams()

  const planId = purchasePlanId ?? ""

  useQueryState(
    [PURCHASE_PLAN_INFO, planId],
    async () => await new ApiPurchasePlans().getPurchasePlanById(planId),
    PURCHASE_PLAN_INFO,
    {
      refetchOnMount: "always",
      retry: 0
    }
  )

  const { data, loading, error } = useAppSelector((state) => state.purchasePlan.purchasePlanInfo)

  const { identifiersDefinition, identifiersCleanup } = usePurchasePlanIdentifiers()

  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const {
    isValidPurchasePlan,
    purchasePlanSummary,
    purchasePlanForm,
    benefits,
    pricingVariables,
    overWritePurchasePlanData,
    isDevMode,
    setDevModePricingVariablesToSubmition,
    devModePricing
  } = usePurchasePlan()

  const onProductChange = useCallback(
    (productId: string) => {
      pricingVariables.resetList()

      identifiersDefinition(productId, data.id)
    },
    [data.id]
  )

  const onSuccess = useCallback(() => {
    navigate("/purchase-plans")
    queryClient.invalidateQueries(PURCHASE_PLAN_INFO)
    queryClient.invalidateQueries(planId)
  }, [planId])

  const { onSubmit } = usePurchasePlanRequest(
    "update",
    mapPurchasePlanToSubmition(
      purchasePlanForm.data,
      benefits.benefitsList,
      pricingVariables.purchasePlanPricing,
      pricingVariables.pricingVariablesList,
      devModePricing,
      pricingVariables.pricingUnit
    ),
    onSuccess,
    "Erro ao atualizar plano. Verifique se preencheu todos os dados obrigatórios corretamente e se o limite de caracteres está compatível com o limite estipulado"
  )

  const toggleDevMode = useCallback(() => {
    const { handlePricingChange, purchasePlanPricing } = pricingVariables

    handlePricingChange("isSimplifiedPricing", !purchasePlanPricing.isSimplifiedPricing)

    handlePricingChange("fromPriceUnit", "")
  }, [pricingVariables])

  useEffect(() => {
    overWritePurchasePlanData(data)

    identifiersDefinition(data.product.id, data.id)
  }, [data])

  useEffect(() => {
    window.scrollTo(0, 0)

    return () => {
      identifiersCleanup()
    }
  }, [])

  return (
    <PageLayout title="Planos" isLoading={loading}>
      <ContentContainer>
        <Tabs defaultActiveTabId={tabIds.plans}>
          {!loading && !error && (
            <>
              <CardContainer>
                <CreatePlanTabList
                  isBenefitStepDisabled={!isValidPurchasePlan}
                  isUpdate
                  toggleDevMode={toggleDevMode}
                  isDevMode={isDevMode}
                />
                <TabsContent triggerId={tabIds.plans}>
                  <PurchasePlanForm
                    {...purchasePlanForm}
                    headingText={planFormHeadingText.update}
                    onProductChange={onProductChange}
                    pricingUnit={pricingVariables.pricingUnit}
                    isDevMode={isDevMode}
                    isUpdate
                  >
                    <SimplePricingVariablesList
                      {...pricingVariables}
                      isDevMode={isDevMode}
                      selectedProduct={purchasePlanForm.data.product}
                      isUpdate
                    />
                    <DevModePricingVariables
                      isDevMode={isDevMode}
                      setDevModePricingVariablesToSubmition={setDevModePricingVariablesToSubmition}
                    />
                  </PurchasePlanForm>
                </TabsContent>

                <TabsContent triggerId={tabIds.benefits}>
                  <Benefits {...benefits} onSubmit={onSubmit} />
                </TabsContent>
              </CardContainer>

              <PlanSummary {...purchasePlanSummary} initialData={data} isUpdate />
            </>
          )}

          {!loading && error && (
            <CardContainer>
              <CreatePlanTabList isBenefitStepDisabled />
              <TabsContent triggerId={tabIds.plans}>
                <ErrorContainer>
                  <BodyText>Plano não encontrado!</BodyText>
                </ErrorContainer>
              </TabsContent>
            </CardContainer>
          )}
        </Tabs>
      </ContentContainer>
    </PageLayout>
  )
}

export default memo(PurchasePlanUpdate)
